import React, { createContext, useContext, useReducer, useCallback, useEffect, useState } from 'react';
import posthog from 'posthog-js';
import axiosInstance from '../../Pages/axiosInstance';




interface ApiNotification {
  id: number;
  type: string;
  title: string | null;
  message: string;
  created_at: string;
  is_read: boolean;
}

interface Notification {
  id: number;
  title: string;
  message: string;
  type: 'error' | 'warning' | 'info' | 'success';
  timestamp: string;
  read: boolean;
}

interface NotificationResponse {
  total: number;
  total_not_read: number;
  notifications: ApiNotification[];
  skip: number;
  limit: number;
  type_counts?: {
    error: number;
    warning: number;
    info: number;
    success: number;
  };
}

interface NotificationContextType {
  notifications: Notification[];
  totalNotifications: number;
  totalUnreadNotifications: number;
  filteredTotal: number;
  readTypeCounts: {
    [key: string]: number;
  };
  typeCounts: {
    error: number;
    warning: number;
    info: number;
    success: number;
  };
  isLoading: boolean;
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => void;
  markAllAsRead: () => void;
  markAsRead: (id: number) => void;
  clearNotifications: () => void;
  refreshNotifications: (skip?: number, limit?: number, isRead?: boolean | null, notificationType?: string | null) => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

const notificationReducer = (state: Notification[], action: any) => {
  switch (action.type) {
    case 'ADD_NOTIFICATION':
      // Sprawdzamy czy powiadomienie o danym ID już istnieje
      const exists = state.some(n => n.id === action.payload.id);
      if (exists) {
        return state;
      }
      // Dodajemy nowe powiadomienie na początek listy
      return [action.payload, ...state];
    case 'MARK_ALL_READ':
      return state.map(notification => ({ ...notification, read: true }));
    case 'CLEAR_ALL':
      return [];
    case 'INIT_NOTIFICATIONS':
      return action.payload;
    case 'MARK_AS_READ':
      return state.map(notification =>
        notification.id === action.payload
          ? { ...notification, read: true }
          : notification
      );
    case 'SET_NOTIFICATIONS':
      return action.payload;
    default:
      return state;
  }
};

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, dispatch] = useReducer(notificationReducer, []);
  const [totalNotifications, setTotalNotifications] = useState(0);
  const [totalUnreadNotifications, setTotalUnreadNotifications] = useState(0);
  const [filteredTotal, setFilteredTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [lastFilterState, setLastFilterState] = useState<{
    skip: number;
    limit: number;
    isRead: boolean | null;
    notificationType: string | null;
  } | null>(null);
  const [readTypeCounts, setReadTypeCounts] = useState<{ [key: string]: number }>({});
  const [typeCounts, setTypeCounts] = useState({
    error: 0,
    warning: 0,
    info: 0,
    success: 0
  });

  // Add ElectricSQL integration with useShape hook - REMOVED

  // Zachowujemy istniejącą funkcję fetchNotifications do paginacji
  const fetchNotifications = useCallback(async (
    skip: number = 0,
    limit: number = 10,
    isRead: boolean | null = null,
    notificationType: string | null = null
  ) => {
    // Store the current filter state to help with debugging
    setLastFilterState({ skip, limit, isRead, notificationType });

    setIsLoading(true);
    try {
      // Clear existing notifications immediately to prevent display of outdated data
      dispatch({ type: 'SET_NOTIFICATIONS', payload: [] });

      const token = localStorage.getItem('token');
      if (!token) {
        setTotalNotifications(0);
        setTotalUnreadNotifications(0);
        setFilteredTotal(0);
        setTypeCounts({ error: 0, warning: 0, info: 0, success: 0 });
        return;
      }

      // Build query parameters
      const queryParams = new URLSearchParams();
      queryParams.append('skip', skip.toString());
      queryParams.append('limit', limit.toString());

      if (isRead !== null) {
        queryParams.append('is_read', isRead.toString());
      }

      if (notificationType && notificationType !== 'all') {
        // The API expects notification_type as a parameter that can be repeated
        // for multiple types, but we're just sending one type for now
        queryParams.append('notification_type', notificationType);
      }

      console.log('Fetching notifications with params:', queryParams.toString());

      const response = await axiosInstance.get<NotificationResponse>(
        `/notifications/?${queryParams.toString()}`
      );

      console.log('API response:', response.data);

      const { notifications: apiNotifications, total, total_not_read, type_counts } = response.data;

      // Store the total count of notifications that match the current filter criteria
      setFilteredTotal(total);

      // Update type counts if available
      if (type_counts) {
        setTypeCounts(type_counts);
      }

      // Calculate and set read/type combination counts
      // This helps with pagination and filtering when both read status and type are combined
      if (apiNotifications && apiNotifications.length > 0) {
        const combinedCounts: { [key: string]: number } = {};

        // Initialize counters
        ['error', 'warning', 'info', 'success'].forEach(type => {
          combinedCounts[`read_${type}`] = 0;
          combinedCounts[`unread_${type}`] = 0;
        });

        // Count notifications by read status and type
        apiNotifications.forEach(notification => {
          const readKey = notification.is_read ? 'read' : 'unread';
          const countKey = `${readKey}_${notification.type}`;

          if (combinedCounts[countKey] !== undefined) {
            combinedCounts[countKey]++;
          } else {
            combinedCounts[countKey] = 1;
          }
        });

        setReadTypeCounts(combinedCounts);
      }

      // Verify that notifications match the requested filters and transform them
      if (notificationType && notificationType !== 'all') {
        // Double-check that all notifications have the correct type
        const invalidTypes = apiNotifications.filter(n => n.type !== notificationType);
        if (invalidTypes.length > 0) {
          console.error('API returned notifications with invalid types:', invalidTypes);
          console.warn('Filtering out notifications with invalid types on client side');
          // Filter out notifications with invalid types
          const validNotifications = apiNotifications.filter(n => n.type === notificationType);
          apiNotifications.length = 0;
          apiNotifications.push(...validNotifications);
        }
      }

      if (isRead !== null) {
        // Double-check that all notifications have the correct read status
        const invalidReadStatus = apiNotifications.filter(n => n.is_read !== isRead);
        if (invalidReadStatus.length > 0) {
          console.error('API returned notifications with invalid read status:', invalidReadStatus);
          console.warn('Filtering out notifications with invalid read status on client side');
          // Filter out notifications with invalid read status
          const validNotifications = apiNotifications.filter(n => n.is_read === isRead);
          apiNotifications.length = 0;
          apiNotifications.push(...validNotifications);
        }
      }

      const transformedNotifications: Notification[] = apiNotifications.map(notification => ({
        id: notification.id,
        title: notification.title || 'Bez tytułu',
        message: notification.message,
        type: notification.type as 'error' | 'warning' | 'info' | 'success',
        timestamp: notification.created_at,
        read: notification.is_read
      }));

      // Set notifications to exactly what the API returned (after client-side validation)
      dispatch({ type: 'SET_NOTIFICATIONS', payload: transformedNotifications });

      // Set total counts
      setTotalNotifications(total);
      setTotalUnreadNotifications(total_not_read);

      // If we've filtered notifications client-side due to API inconsistencies,
      // update filteredTotal to match the actual number of valid notifications
      if (transformedNotifications.length !== apiNotifications.length) {
        setFilteredTotal(transformedNotifications.length);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);


  // Sprawdzaj token co sekundę przez pierwsze 10 sekund po zamontowaniu komponentu
  useEffect(() => {
    let attempts = 0;
    const maxAttempts = 10;

    const checkToken = setInterval(() => {
      const token = localStorage.getItem('token');
      if (token) {
        fetchNotifications();
        clearInterval(checkToken);
      } else if (attempts >= maxAttempts) {
        clearInterval(checkToken);
      }
      attempts++;
    }, 1000);

    return () => clearInterval(checkToken);
  }, [fetchNotifications]);

  // Nasłuchuj na zmiany w localStorage
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'token' && e.newValue) {
        fetchNotifications();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Inicjalne sprawdzenie
    const token = localStorage.getItem('token');
    if (token) {
      fetchNotifications();
    }

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [fetchNotifications]);

  const markAsRead = useCallback(async (id: number) => {
    try {
      await axiosInstance.post(`/notifications/${id}/read`);
      dispatch({ type: 'MARK_AS_READ', payload: id });
      // Update unread count when marking a notification as read
      setTotalUnreadNotifications(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    try {
      await axiosInstance.post('/notifications/mark_all_as_read');
      dispatch({ type: 'MARK_ALL_READ' });
      // Set unread count to 0 when marking all as read
      setTotalUnreadNotifications(0);
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  }, []);

  // Remove unused functions or keep them if needed for other features
  const addNotification = useCallback((notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => {
    console.warn('addNotification is deprecated - notifications are now managed via API');
  }, []);

  const clearNotifications = useCallback(() => {
    console.warn('clearNotifications is deprecated - notifications are now managed via API');
  }, []);

  return (
    <NotificationContext.Provider value={{
      notifications,
      totalNotifications,
      totalUnreadNotifications,
      filteredTotal,
      readTypeCounts,
      typeCounts,
      isLoading,
      addNotification,
      markAllAsRead,
      markAsRead,
      clearNotifications,
      refreshNotifications: fetchNotifications
    }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};